/* istanbul ignore file */
import React, { useMemo } from 'react';
import { Grid2 as Grid } from '@mui/material';
import { FormFieldsData, DateInput, TextInput } from 'framework/form';
import RemoveListItemButton from 'domain/projects/project/weeklyreports/shared/button/RemoveListItemButton';
import { TextButton } from 'framework/ui/ui-builder';
import useInputRowWithSpecificId from './useInputRowWithSpecificId';
import type { MilestoneType } from './inputTypes';

interface MilestoneInputRowProps {
  data: MilestoneType;
  id: number;
  formData: any;
  updateFormData: (source: string, newFormData: any) => void;
  getErrorMessages: (fieldSource: string) => string[];
}

export const getMilestonesSource = (id: number) => {
  return `weeklyReportData.milestones[${id}]`;
};

const MilestoneInputRow = ({ data, id, formData, updateFormData, getErrorMessages }: MilestoneInputRowProps) => {
  const source = getMilestonesSource(id);
  const { getOnChangeCallback, removeRow } = useInputRowWithSpecificId({
    data,
    source,
    formData,
    id,
    updateFormData,
    listSource: 'milestones',
  });

  const getAdditionalFields = (newFormData: FormFieldsData, fieldName: string) => {
    return { completed: !!newFormData[`${source}.${fieldName}`] };
  };

  const isDisabledSpecific = useMemo(() => {
    return data.fromPreviousReport && data.completed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.fromPreviousReport, data.id]);

  return (
    <Grid container spacing={2} style={id === 0 ? { alignItems: 'flex-end' } : {}}>
      <Grid size={{ xs: 12, md: 1 }}>
        <TextInput
          source={`${source}.id`}
          label={id === 0 ? 'ID' : ''}
          value={data.id.toString()}
          disabled={id !== 0 || !formData.firstReport}
          onChangeCallback={getOnChangeCallback('id')}
          isRequired
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <TextButton
          source={`${source}.description`}
          label={id === 0 ? 'Description' : ''}
          value={data.description}
          disabled={data.fromPreviousReport}
          onChangeCallback={getOnChangeCallback('description')}
          isRequired
          title="Description"
          errors={getErrorMessages(`${source}.description`)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <DateInput
          source={`${source}.plannedDate`}
          label={id === 0 ? 'Planned date' : ''}
          value={data.plannedDate}
          disabled={isDisabledSpecific}
          onChangeCallback={getOnChangeCallback('plannedDate')}
          isRequired
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2 }} style={{ maxWidth: '16%' }}>
        <DateInput
          source={`${source}.currentlyPlannedDate`}
          label={id === 0 ? 'Currently planned date' : ''}
          value={data.currentlyPlannedDate}
          disabled={isDisabledSpecific}
          onChangeCallback={getOnChangeCallback('currentlyPlannedDate')}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <TextButton
          source={`${source}.comments`}
          value={data.comments}
          label={id === 0 ? 'Comments' : ''}
          title="Comments"
          onChangeCallback={getOnChangeCallback('comments')}
          disabled={isDisabledSpecific}
          errors={getErrorMessages(`${source}.comments`)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <DateInput
          source={`${source}.endDate`}
          label={id === 0 ? 'End date' : ''}
          value={data.endDate}
          disabled={isDisabledSpecific}
          onChangeCallback={getOnChangeCallback('endDate', getAdditionalFields)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 1 }}>
        <RemoveListItemButton
          isVisible={!data.fromPreviousReport}
          onClickCallback={removeRow}
          isFirstRow={id === 0}
          sx={{ marginTop: id ? '6px !important' : 0, marginBottom: id ? 0 : '27px' }}
        />
      </Grid>
    </Grid>
  );
};

export default MilestoneInputRow;
